import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { object, func, array } from "prop-types";
import get from "lodash/get";

import { StoryHeader } from "../../molecules";
import { PhotoStoryElement } from "../../../arrow";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import { EntityListingStory } from "../../atoms/EntityListingStory";
import PhotoStorySlider from "../../molecules/PhotoStorySlider";

import { useDeviceType } from "../../customHooks/useDeviceType";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { getPublishedDate, getPublishedTime } from "../../utils";

import "./photo-story.m.css";

const MetypeCommentsWidgetWrapper = lazy(() => import("../story-elements/metype-comments"));

const PhotoStory = ({ story, config, adWidget, adPlaceholder, hasAccess, recommendedStories }) => {
  const metypeTemplates = get(config, ["publisher-attributes", "metypeConfig", "storyTemplate"], []);
  const shouldLoadMetypeComments =
    get(config, ["publisher-attributes", "enableMetype"], false) &&
    Array.isArray(metypeTemplates) &&
    metypeTemplates.includes(story["story-template"]);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const observerRef = useRef(null);

  useEffect(() => {
    const photoElements = document.querySelectorAll("[data-photo-index]");
    let lastIndex = -1;

    if ("IntersectionObserver" in window) {
      observerRef.current = new window.IntersectionObserver(
        (entries) => {
          let maxRatio = 0;
          let currentIndex = lastIndex;
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
              maxRatio = entry.intersectionRatio;
              currentIndex = parseInt(entry.target.getAttribute("data-photo-index"), 10);
            }
          });
          if (currentIndex !== lastIndex && currentIndex !== undefined && currentIndex !== -1) {
            // Update URL
            const url = new URL(window.location);
            url.searchParams.set("photo", currentIndex + 1); // 1-based index
            window.history.replaceState({}, "", url);
            lastIndex = currentIndex;
            // Fire GA event if index changed
            const pageType = "story-page";
            const { category, subCategory, contentId } = getCategoryAndSubCategory(
              window.location,
              pageType,
              config,
              story
            );
            const templateName = getTemplateName(story?.["story-template"] || pageType);
            if (typeof window !== "undefined" && window.dataLayer) {
              window.dataLayer.push({
                event: "dl_page_info",
                photo_index: currentIndex + 1,
                cms_author_name: story?.["assignee-name"],
                byline: story?.authors.map((author) => author.name).toString(),
                category: category || "NA",
                sub_category: subCategory || "NA",
                content_id: contentId || "NA",
                template_name: templateName,
                publish_date: getPublishedDate(story?.["published-at"]),
                publish_time: getPublishedTime(story?.["published-at"]),
                login_status: "not-logged-in",
                subscription_status: "not-subscribed", // will be changed
                ad_free: false, // will be changed
                login_wall_enabled: story?.["story-template"] === "magazine",
                platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
              });
            }
          }
        },
        {
          threshold: [0.5], // 50% in view
        }
      );

      photoElements.forEach((el) => observerRef.current.observe(el));
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [story.cards.length, config, story]);

  const { headline, authors } = story;
  const newCards = story.cards;

  const toggleSliderView = (index) => () => {
    setIsSliderOpen(true);
    setCurrentIndex(index);
  };

  const closeSliderView = () => {
    const url = new URL(window.location);
    url.searchParams.set("photo", currentIndex + 1);
    window.history.replaceState({}, "", url);
    setIsSliderOpen(false);
  };

  return (
    <div styleName="story-template-container" className="story-holder">
      <StoryHeader
        story={story}
        headline={headline}
        storyAuthorData={authors}
        publishTime={story?.["published-at"]}
        readTime={story?.["read-time"]}
        showHeroImage={false}
      />
      <EntityListingStory story={story} config={config} />
      <div id="story-wrapper" styleName="photo-story-content">
        {newCards.map((card, index) => {
          return (
            <div
              key={index}
              id={card.id}
              data-photo-index={index}
              styleName={index % 2 !== 0 ? "photo-story-cards" : ""}
            >
              <PhotoStoryElement
                card={card}
                story={story}
                toggleSliderView={toggleSliderView(index)}
                cardIndex={index + 1}
                totalCount={newCards.length}
                indexAd={index}
                isDesktop={useDeviceType() === "desktop"}
                firstCard={index === 0}
                config={config}
              />
            </div>
          );
        })}

        <StoryTags tags={story.tags} template="noBorder" borderTop={false} story={story} />
        {shouldLoadMetypeComments && (
          <Suspense fallback={<div></div>}>
            <MetypeCommentsWidgetWrapper story={story} />
          </Suspense>
        )}
      </div>
      <PhotoStorySlider
        story={story}
        isSliderOpen={isSliderOpen}
        closeSliderView={closeSliderView}
        defaultIndex={currentIndex}
        isUrlChangeNeeded={true}
      />
    </div>
  );
};

PhotoStory.propTypes = {
  story: object,
  config: object,
  adWidget: func,
  adPlaceholder: object,
  hasAccess: func,
  recommendedStories: array,
};

export default PhotoStory;
